import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { usePublish, useSubscribe } from '@atlaskit/rovo-triggers/main';
import type { CustomSkillUpdatePayload } from '@atlaskit/rovo-triggers/types';
import { PlatformSiteAri } from '@atlassian/ari/platform';
import { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { isActionExecutedSuccessfully } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useAssistanceService } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';
import { useRovoSkillsInvalidation } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';

import type { StudioCustomSkillRendererQuery as StudioCustomSkillRendererQueryType } from './__generated__/StudioCustomSkillRendererQuery.graphql';
import { CustomSkillCard, type CustomSkillCardProps } from './custom-skill-card/CustomSkillCard';
import { EditSkillModalButton } from './EditSkillModalButton';
import { getExecutedDataFromChat } from './getExecutedDataFromChat';
import { messages } from './messages';
import {
	cacheResolvedSkillAri,
	forgetResolvedSkillAri,
	getResolvedSkillAri,
} from './resolvedSkillAriCache';

type CustomSkillActionKey =
	| typeof ActionKeys.CreateCustomSkillTool
	| typeof ActionKeys.UpdateCustomSkillTool;

type StudioCustomSkillRendererProps = ActionRendererFor<CustomSkillActionKey>;

type SkillActionResult = {
	skillAri: string;
};

const isSkillActionResult = (data: unknown): data is SkillActionResult =>
	Boolean(data && typeof data === 'object' && 'skillAri' in data);

const getSkillExecutedDataWithSkillAri = (data: unknown) => {
	if (
		!data ||
		typeof data !== 'object' ||
		'skillAri' in data ||
		!('id' in data) ||
		typeof data.id !== 'string'
	)
		return data;

	return {
		...data,
		skillAri: data.id,
	};
};

function SkillDetailsCard({
	skillAri,
	cloudId,
	cardProps,
}: {
	skillAri: string;
	cloudId: string;
	cardProps: CustomSkillCardProps;
}): React.JSX.Element {
	const contextAri = useMemo(
		() => PlatformSiteAri.create({ siteId: cloudId }).toString(),
		[cloudId],
	);

	// When the skill is edited from the edit-skill modal it broadcasts the newly persisted content.
	// We apply that payload directly (no refetch), keeping every card for the same skill in sync.
	const [updatedSkill, setUpdatedSkill] = useState<CustomSkillUpdatePayload['data'] | null>(null);

	useSubscribe({ topic: 'ai-mate' }, (payload) => {
		if (payload.type === 'custom-skill-update' && payload.data.skillAri === skillAri) {
			setUpdatedSkill(payload.data);
		}
	});

	const data = useLazyLoadQuery<StudioCustomSkillRendererQueryType>(
		graphql`
			query StudioCustomSkillRendererQuery($skillAri: ID!, $contextAri: ID!) {
				graphIntegration_skill(skillAri: $skillAri, contextAri: $contextAri)
					@optIn(to: "GraphIntegrationSkillQuery") {
					name
					description
					instructions
					uiMetadata {
						displayName
					}
				}
			}
		`,
		{ skillAri, contextAri },
	);

	const skill = data.graphIntegration_skill;

	if (!skill) {
		return <ChatGenericError errorType="generic" />;
	}

	return (
		<CustomSkillCard
			{...cardProps}
			displayName={
				updatedSkill?.displayName ??
				skill?.uiMetadata?.displayName ??
				skill?.name ??
				cardProps.displayName
			}
			description={updatedSkill?.description ?? skill?.description ?? cardProps.description}
			instructions={updatedSkill?.instructions ?? skill?.instructions ?? cardProps.instructions}
		/>
	);
}

/**
 * Renderer for custom skill create and update actions.
 *
 * Renders the card when the user requests to create or update a custom skill.
 */
export function StudioCustomSkillRenderer(
	actionRenderer: StudioCustomSkillRendererProps,
): React.JSX.Element | null {
	const { invalidateSkillLibrary } = useRovoSkillsInvalidation();
	const publish = usePublish('ai-mate');
	const assistanceService = useAssistanceService();

	const firstAction = actionRenderer.actions[0];
	const invocationId = firstAction?.invocationId;

	const executedData = getSkillExecutedDataWithSkillAri(firstAction?.executedData);

	const hasExecutedSkillAri = isSkillActionResult(executedData);

	// Seeded from the cache: a remount would otherwise lose the ari, and with it the Edit button.
	const [resolvedSkillAri, setResolvedSkillAri] = useState<string | undefined>(() =>
		getResolvedSkillAri(invocationId),
	);
	const [isConfirming, setIsConfirming] = useState(false);

	// The action carries the ari itself now, so the cached copy is dead weight.
	useEffect(() => {
		if (hasExecutedSkillAri && invocationId) {
			forgetResolvedSkillAri(invocationId);
		}
	}, [hasExecutedSkillAri, invocationId]);

	if (!firstAction) return null;

	const { cloudId, conversationId, getStatus, onConfirm, onCustomDataConfirm } = actionRenderer;

	const { displayName, description, instructions } = firstAction.data;

	const status = getStatus(firstAction);

	const isUpdateAction = firstAction.key === ActionKeys.UpdateCustomSkillTool;

	// `firstAction.status` is set for a historical action; `status` reflects an in-session
	// resolution. Also a success signal — `deriveInvocationStatus` reports 'errored' unless
	// `isActionExecutedSuccessfully` holds.
	const hasFinishedSuccessfully = status === 'finished' || firstAction.status === 'finished';

	const dataSkillAri = isSkillActionResult(firstAction.data)
		? firstAction.data.skillAri
		: undefined;
	const executedSkillAri = hasExecutedSkillAri ? executedData.skillAri : undefined;

	// update card uses skill ari from `.data`. create card uses skill ari from executedData OR from cache
	const skillAri = isUpdateAction ? dataSkillAri : (executedSkillAri ?? resolvedSkillAri);

	const persistedSkillAri = hasFinishedSuccessfully ? skillAri : undefined;

	const showEditButton = Boolean(persistedSkillAri) && fg('chat_edit_skill_modal');

	const isDisabled =
		!showEditButton &&
		(status !== 'idle' ||
			(firstAction.status && ['errored', 'cancelled', 'finished'].includes(firstAction.status)));
	const isSubmitting = status === 'started' || isConfirming;

	/**
	 * The resolve response carries no result for these tools, so a newly created skill's ARI is only
	 * readable from the persisted action in history. Silent on failure: the skill was still created,
	 * and the ARI is picked up on the next page load.
	 */
	const resolveSkillAriFromHistory = async () => {
		// Surfaces without a configured service get the context default, which has no base URL.
		if (!conversationId || !assistanceService.getConfig().baseUrl) {
			return;
		}

		try {
			const chat = await assistanceService.getChat(conversationId);
			const persistedData = getSkillExecutedDataWithSkillAri(
				getExecutedDataFromChat(chat, firstAction.invocationId),
			);

			if (isSkillActionResult(persistedData)) {
				setResolvedSkillAri(persistedData.skillAri);
				cacheResolvedSkillAri(firstAction.invocationId, persistedData.skillAri);
			}
		} catch {
			// Non-fatal, see above.
		}
	};

	const handleConfirm = async () => {
		setIsConfirming(true);

		try {
			// A skill update must only be reflected in the UI when the backend tool actually
			// succeeded. `onConfirm` resolves even when the tool returns a failure result, so for
			// the sync path we resolve via `onCustomDataConfirm`, whose content processor reports
			// execution success — the same signal the backend success broadcast keys off.
			const syncSkillUpdate =
				firstAction.key === ActionKeys.UpdateCustomSkillTool &&
				fg('studio_sync_custom_skill_chat_update');

			let updateSucceeded = false;
			if (syncSkillUpdate) {
				updateSucceeded =
					(await onCustomDataConfirm(
						firstAction,
						firstAction.data,
						isActionExecutedSuccessfully,
					)) === true;
			} else {
				await onConfirm(firstAction);
			}

			invalidateSkillLibrary();

			// The lookup only exists to discover the ari, so knowing one already makes it dead work —
			// an update reads it off its payload, and a create's own result carries it after a reload.
			if (!skillAri && fg('chat_edit_skill_modal')) {
				await resolveSkillAriFromHistory();
			}

			// `updateSucceeded` is only ever true for a confirmed, successful skill update. The
			// `'skillAri' in` check narrows `firstAction.data` to the update payload with no cast;
			// the action isn't a key-discriminated union, so a `key ===` check would not narrow.
			if (updateSucceeded && 'skillAri' in firstAction.data) {
				const { skillAri, name, tools } = firstAction.data;
				publish({
					type: 'custom-skill-update',
					source: 'studio-custom-skill-action-ui',
					data: { skillAri, name, displayName, description, instructions, tools },
				});
			}
		} finally {
			setIsConfirming(false);
		}
	};
	const cardProps: CustomSkillCardProps = {
		displayName,
		description,
		instructions,
		headerLabel: isUpdateAction ? messages.updateSkillHeaderLabel : messages.createSkillHeaderLabel,
		confirmLabel: isUpdateAction ? messages.updateLabel : messages.confirmLabel,
		isDisabled,
		isSubmitting,
		onConfirm: handleConfirm,
		confirmSlot:
			showEditButton && persistedSkillAri ? (
				<EditSkillModalButton
					cloudId={cloudId}
					skillAri={persistedSkillAri}
					cardType={isUpdateAction ? 'update' : 'create'}
				/>
			) : undefined,
	};

	if (persistedSkillAri) {
		return (
			<Suspense fallback={<CustomSkillCard {...cardProps} isLoadingDetails />}>
				<SkillDetailsCard skillAri={persistedSkillAri} cloudId={cloudId} cardProps={cardProps} />
			</Suspense>
		);
	}

	return <CustomSkillCard {...cardProps} />;
}
